import React, { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { CustomerEvent, CustomerEventType, CremationStatus } from '../../types';
import { benefitsTimelineLabel } from '../benefitsStatus';
import {
  Phone,
  PhoneMissed,
  Voicemail,
  Mail,
  ChevronDown,
  ChevronUp,
  FileText,
  Eye,
  PenTool,
  CreditCard,
  Star,
  Send,
  Truck,
  StickyNote,
  Wand2,
  Loader2,
  X,
  Globe,
  Paperclip,
  Heart,
  Landmark,
} from 'lucide-react';
import { asCallContext, callContextDetails, callContextText } from '../../inbox/callContext';
import { translateText } from './translateApi';
import { EmailHtmlBody } from './EmailHtmlBody';
import { emailPlainText, htmlOnlyBody } from '../emailThreads';
import { supabase } from '../../lib/supabaseClient';
import { EmailDeliveryStatusBadge } from '../../components/EmailDeliveryStatusBadge';
import { SmsDeliveryStatusBadge } from '../../components/SmsDeliveryStatusBadge';
import { useSmsDeliveryStatus } from '../../hooks/useSmsDeliveryStatus';
import { useCallLink } from '../../caller-links/CallLinkContext';
import { isLinkableMessageId } from '../../caller-links/linkCallerFormat';

// ─── Date Divider ────────────────────────────────────────────────────────────

interface DateDividerProps {
  date: string;
}

export function DateDivider({ date }: DateDividerProps) {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="flex-1 h-px bg-border" />
      <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
        {new Date(date).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

// ─── SMS Bubble ──────────────────────────────────────────────────────────────

interface SmsBubbleProps {
  event: CustomerEvent;
  clientColor?: string;
  clientName?: string;
}

export function SmsBubble({ event, clientColor, clientName }: SmsBubbleProps) {
  const isOutbound = event.type === CustomerEventType.TEXT_MESSAGE;
  const [hovered, setHovered] = useState(false);
  const [translation, setTranslation] = useState<string | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const time = new Date(event.timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  // Live delivery status for outbound SMS — updates without page refresh
  // when the message.status_updated webhook flips delivery_status.
  const liveMetadata = useSmsDeliveryStatus(
    isOutbound ? event.aircall_message_id : undefined,
    event.metadata,
  );

  const handleTranslate = async () => {
    if (translation) {
      setShowTranslation(!showTranslation);
      return;
    }
    if (!event.message) return;
    setTranslating(true);
    try {
      const result = await translateText(event.message, 'en', event.id);
      setTranslation(result);
      setShowTranslation(true);
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div
      className={cn('flex flex-col gap-1 max-w-[75%]', isOutbound ? 'ml-auto items-end' : 'mr-auto items-start')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative group">
        <div
          className={cn(
            'rounded-2xl px-3 sm:px-4 py-2.5 text-sm leading-relaxed [overflow-wrap:anywhere]',
            isOutbound
              ? 'bg-primary text-primary-foreground rounded-br-md'
              : 'bg-muted text-foreground rounded-bl-md',
          )}
          style={clientColor ? { borderLeft: `3px solid ${clientColor}` } : undefined}
        >
          {event.message || '(no content)'}
          {showTranslation && translation && (
            <div className={cn(
              'mt-2 pt-2 text-xs italic',
              isOutbound ? 'border-t border-primary-foreground/20' : 'border-t border-border',
            )}>
              {translation}
            </div>
          )}
        </div>
        {(hovered || showTranslation) && event.message && (
          <button
            onClick={handleTranslate}
            disabled={translating}
            className={cn(
              'absolute -top-2 p-1 rounded-full shadow-sm border bg-background text-muted-foreground hover:text-foreground hover:bg-muted transition-all',
              isOutbound ? '-left-2' : '-right-2',
            )}
            title={showTranslation ? 'Hide translation' : 'Translate to English'}
          >
            {translating ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Wand2 className="h-3 w-3" />
            )}
          </button>
        )}
      </div>
      <div className="flex items-center gap-1.5 px-1">
        <span className="text-[11px] text-muted-foreground">{time}</span>
        {clientName && (
          <span className="text-[11px] text-muted-foreground">
            &middot; {clientName}
          </span>
        )}
        {event.user_name && (
          <span className="text-[11px] text-muted-foreground">
            &middot; {event.user_name}
          </span>
        )}
        {isOutbound && (
          <SmsDeliveryStatusBadge metadata={liveMetadata} />
        )}
      </div>
    </div>
  );
}

// ─── Phone Event Bubble ──────────────────────────────────────────────────────

interface PhoneEventBubbleProps {
  event: CustomerEvent;
  clientColor?: string;
  clientName?: string;
}

// Aircall's missed_call_reason, in the words staff would use.
const MISSED_CALL_REASONS: Record<string, string> = {
  out_of_opening_hours: 'After hours',
  short_abandoned: 'Hung up right away',
  abandoned_in_ivr: 'Hung up in the menu',
  abandoned_in_classic: 'Hung up while waiting',
  no_available_agent: 'No one available',
  agents_did_not_answer: 'No one picked up',
};

export interface CallDescription {
  missed: boolean;
  voicemail: boolean;
  title: string;
  reason: string | null;
  /**
   * What the webhook recorded about the call (T-058), for the expanded card:
   * outcome, time on the line, line, ad session, repeat, hours.
   */
  details: { label: string; value: string }[];
}

/**
 * How a call card reads. Worded like the inbox preview (`conversationPreview`)
 * so a call says the same thing in both places: "Missed call" inbound,
 * "Called, no answer" outbound, "Voicemail" when one was left.
 *
 * Only an explicit non-'answered' status counts as missed — customer_events
 * phone rows and pre-communications history carry no status, and must keep
 * rendering as ordinary calls.
 */
export function describeCall(event: CustomerEvent): CallDescription {
  const name = event.recipient_name;
  const status = event.call_status;
  const missed = !!status && status !== 'answered';
  const voicemail = status === 'voicemail';
  const inbound = event.direction === 'inbound';
  const rawReason = event.metadata?.missed_call_reason;
  const aircallReason = missed && typeof rawReason === 'string'
    ? MISSED_CALL_REASONS[rawReason] ?? null
    : null;
  // The call context (T-058) wins over Aircall's reason, which prod never
  // sends: "Hung up in menu · 12 s · Ad call: cremation montreal".
  const ctx = asCallContext(event.metadata?.call_context);
  const facts = { direction: event.direction, status, durationSeconds: event.duration_seconds };
  const contextLine = callContextText(ctx, facts);
  const reason = contextLine ?? aircallReason;
  const details = callContextDetails(ctx, facts);

  if (!missed) {
    return {
      missed,
      voicemail,
      title: name ? `Call with ${name}` : 'Phone call',
      reason: contextLine,
      details,
    };
  }
  if (voicemail) {
    return { missed, voicemail, title: name ? `Voicemail from ${name}` : 'Voicemail', reason, details };
  }
  if (inbound) {
    return { missed, voicemail, title: name ? `Missed call from ${name}` : 'Missed call', reason, details };
  }
  return {
    missed,
    voicemail,
    title: name ? `Called ${name}, no answer` : 'Called, no answer',
    reason,
    details,
  };
}

interface CallSessionData {
  summary: string | null;
  transcription_segments: Array<{
    speaker: string;
    text: string;
    start_time: number;
    end_time: number;
  }> | null;
}

export function PhoneEventBubble({ event, clientColor, clientName }: PhoneEventBubbleProps) {
  const [expanded, setExpanded] = useState(false);
  const [callData, setCallData] = useState<CallSessionData | null>(null);
  const [loadingCall, setLoadingCall] = useState(false);
  const [showTranscription, setShowTranscription] = useState(false);
  const openCallLink = useCallLink();
  const time = new Date(event.timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  const loadCallSession = useCallback(async () => {
    if (callData || !event.source_id || loadingCall) return;
    setLoadingCall(true);
    try {
      const { data } = await supabase
        .from('call_sessions')
        .select('summary, transcription_segments')
        .eq('id', event.source_id)
        .single();
      setCallData(data || null);
    } catch {
      // ignore
    } finally {
      setLoadingCall(false);
    }
  }, [event.source_id, callData, loadingCall]);

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) void loadCallSession();
  };

  const hasTranscription = callData?.transcription_segments && callData.transcription_segments.length > 0;
  const call = describeCall(event);
  const CallIcon = call.voicemail ? Voicemail : call.missed ? PhoneMissed : Phone;

  return (
    <>
      <div className="flex justify-center py-1">
        <button
          onClick={handleExpand}
          className={cn(
            'flex flex-col items-center gap-1 rounded-xl border bg-background px-3 sm:px-4 py-2 shadow-sm hover:bg-muted/50 transition-colors cursor-pointer w-full max-w-md',
            call.missed && 'border-red-200 bg-red-50/60 hover:bg-red-50 dark:border-red-900/60 dark:bg-red-950/20',
          )}
          style={clientColor ? { borderLeft: `3px solid ${clientColor}` } : undefined}
        >
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center">
            <CallIcon className={cn('h-3.5 w-3.5 shrink-0', call.missed ? 'text-red-600' : 'text-green-600')} />
            <span className={cn('text-xs font-medium truncate', call.missed && 'text-red-700 dark:text-red-400')}>
              {call.title}
            </span>
            <span className="text-[11px] text-muted-foreground shrink-0">{time}</span>
            {call.reason && call.details.length === 0 && (
              <span className="text-[11px] text-muted-foreground shrink-0">&middot; {call.reason}</span>
            )}
            {clientName && (
              <span className="text-[11px] text-muted-foreground shrink-0">&middot; {clientName}</span>
            )}
            {expanded ? (
              <ChevronUp className="h-3 w-3 text-muted-foreground shrink-0" />
            ) : (
              <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
            )}
          </div>
          {/* The call context (T-058) gets its own line: "Hung up in menu ·
              12 s · Ad call: cremation montreal". Opening the card lists it. */}
          {call.reason && call.details.length > 0 && (
            <span className="text-[11px] leading-snug text-muted-foreground" data-testid="call-reason">
              {call.reason}
            </span>
          )}
          {expanded && (
            <div className="text-xs text-muted-foreground text-left w-full pt-2 border-t mt-1">
              {call.details.length > 0 && (
                <dl
                  className="mb-2 grid grid-cols-[96px_minmax(0,1fr)] gap-x-2.5 gap-y-0.5 text-[11px]"
                  data-testid="call-details"
                >
                  {call.details.map((d) => (
                    <React.Fragment key={d.label}>
                      <dt className="text-muted-foreground">{d.label}</dt>
                      <dd className="text-foreground [overflow-wrap:anywhere]">{d.value}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
              {loadingCall && <p className="text-[11px] italic"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Loading...</p>}
              {callData?.summary && (
                <p className="mb-2 text-[12px] leading-relaxed">{callData.summary}</p>
              )}
              {!callData?.summary && event.message && <p className="mb-1">{event.message}</p>}
              {event.user_name && (
                <p className="text-[11px]">{call.missed ? 'Rang' : 'Agent'}: {event.user_name}</p>
              )}
              {event.contact_detail && (
                <p className="text-[11px]">{event.contact_detail}</p>
              )}
              {hasTranscription && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowTranscription(true); }}
                  className="mt-2 text-[11px] text-blue-600 hover:text-blue-800 underline"
                >
                  View Transcription
                </button>
              )}
              {openCallLink && isLinkableMessageId(event.id) && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); openCallLink({ communicationId: String(event.id) }); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      openCallLink({ communicationId: String(event.id) });
                    }
                  }}
                  className={cn('mt-2 block text-[11px] text-blue-600 hover:text-blue-800 underline', hasTranscription && 'mt-1')}
                  data-testid="link-this-call"
                >
                  Link this call…
                </span>
              )}
            </div>
          )}
        </button>
      </div>

      {/* Transcription Modal */}
      {showTranscription && callData?.transcription_segments && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowTranscription(false)}
        >
          <div
            className="bg-background rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold">
                Call Transcription {event.recipient_name ? `— ${event.recipient_name}` : ''}
              </h3>
              <button
                onClick={() => setShowTranscription(false)}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-y-auto px-4 py-3 space-y-2">
              {callData.transcription_segments.map((seg, i) => (
                <div key={i} className={cn(
                  'flex flex-col',
                  seg.speaker === 'internal' ? 'items-end' : 'items-start',
                )}>
                  <span className="text-[10px] text-muted-foreground mb-0.5">
                    {seg.speaker === 'internal' ? (event.user_name || 'Agent') : (event.recipient_name || 'Caller')}
                  </span>
                  <div className={cn(
                    'rounded-lg px-3 py-1.5 max-w-[85%] text-xs',
                    seg.speaker === 'internal'
                      ? 'bg-blue-100 text-blue-900'
                      : 'bg-muted text-foreground',
                  )}>
                    {seg.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Email Event Bubble ──────────────────────────────────────────────────────

// Strip the quoted history Gmail/Outlook append to replies ("On … wrote:",
// "> …", "-----Original Message-----", "From:/De:" headers) so the feed shows
// just the new content. Non-destructive — the full body is revealed on expand.
function splitQuotedText(body: string): { main: string; hasQuoted: boolean } {
  const lines = body.split('\n');
  const marker =
    /^(>|On\b.*\bwrote:|Le\b.*a\s+écrit\s*:|-{2,}\s*Original Message\s*-{2,}|_{5,}|From:\s|De\s*:\s)/i;
  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    if (marker.test(lines[i].trim())) {
      cut = i;
      break;
    }
  }
  if (cut === -1) return { main: body, hasQuoted: false };
  const main = lines.slice(0, cut).join('\n').trim();
  // Whole body is quoted (e.g. a forward) — keep it rather than blanking out.
  if (!main) return { main: body, hasQuoted: false };
  return { main, hasQuoted: true };
}

interface EmailEventBubbleProps {
  event: CustomerEvent;
  clientColor?: string;
  clientName?: string;
}

export function EmailEventBubble({ event, clientColor, clientName }: EmailEventBubbleProps) {
  // Historical email rows (pre-ingestion) carry no direction and were always
  // outbound, so treat null as outbound → keeps them on the right.
  const isOutbound = event.direction !== 'inbound';
  const [expanded, setExpanded] = useState(false);
  const [translation, setTranslation] = useState<string | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const time = new Date(event.timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  // Collapse the blank-line padding plaintext emails carry (\r\n\r\n between
  // every line) so the bubble stays compact.
  const htmlBody = htmlOnlyBody(event);
  const body = emailPlainText(event)
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
  const attachmentCount = Number(event.metadata?.attachment_count) || 0;
  const hasBody = !!body;

  // Real Gmail-ingested conversation emails (the poller stamps metadata.source)
  // show their body by default. Automated/transactional emails (MailService,
  // legacy events) are boilerplate and collapse to the subject. Either can be
  // toggled open to read the full text.
  const isConversational = event.metadata?.source === 'gmail_ingestion';

  // Quoted reply history is hidden by default, revealed on expand.
  const { main: bodyMain, hasQuoted } = splitQuotedText(body);
  const mainIsLong = bodyMain.length > 280 || (bodyMain.match(/\n/g)?.length ?? 0) > 6;

  // What renders in the body region:
  //   automated + collapsed → nothing (subject only)
  //   automated + expanded  → full body
  //   conversation collapsed → quote-stripped new content
  //   conversation expanded  → full body incl. quoted history
  const renderedBody = expanded ? body : isConversational ? bodyMain : '';
  const bodyVisible = hasBody && !!renderedBody;
  const clampBody = isConversational && !expanded && mainIsLong;
  const showToggle = hasBody && (!isConversational || hasQuoted || mainIsLong);
  const toggleLabel = expanded
    ? isConversational
      ? 'Show less'
      : 'Hide'
    : isConversational
      ? hasQuoted
        ? 'Show quoted text'
        : 'Show more'
      : 'Show email';

  const handleTranslate = async () => {
    if (translation) {
      setShowTranslation(!showTranslation);
      return;
    }
    if (!bodyMain) return;
    setTranslating(true);
    try {
      const result = await translateText(bodyMain, 'en', event.id);
      setTranslation(result);
      setShowTranslation(true);
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-1',
        isOutbound ? 'ml-auto items-end' : 'mr-auto items-start',
      )}
      // Inline maxWidth (not a Tailwind arbitrary class — the comma in min()
      // isn't reliably generated by the JIT, and sm: doesn't apply in this feed).
      // Visible-body emails get a narrow reading column; collapsed/subject-only
      // emails hug their subject up to a wider cap. min(%, rem) keeps it
      // mobile-safe without a breakpoint.
      style={{ maxWidth: bodyVisible ? 'min(85%, 20rem)' : 'min(90%, 34rem)' }}
    >
      <div
        className={cn(
          'rounded-2xl border px-3 sm:px-4 py-2.5 text-sm shadow-sm',
          // Visible-body emails fill the bounded wrapper; collapsed/subject-only
          // emails hug content.
          bodyVisible && 'w-full',
          isOutbound
            ? 'bg-primary/5 border-primary/20 rounded-br-md'
            : 'bg-muted border-border rounded-bl-md',
        )}
        style={clientColor ? { borderLeft: `3px solid ${clientColor}` } : undefined}
      >
        {/* Header: icon + subject + attachment chip + translate */}
        <div className="flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5 text-purple-600 shrink-0" />
          <span className="text-xs font-semibold flex-1 min-w-0 break-words">
            {event.subject || '(no subject)'}
          </span>
          {attachmentCount > 0 && (
            <span
              className="flex items-center gap-0.5 text-[11px] text-muted-foreground shrink-0"
              title={`${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''} — open in Gmail to download`}
            >
              <Paperclip className="h-3 w-3" />
              {attachmentCount}
            </span>
          )}
          {bodyVisible && (
            <button
              onClick={handleTranslate}
              disabled={translating}
              className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
              title={showTranslation ? 'Hide translation' : 'Translate to English'}
            >
              {translating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
            </button>
          )}
        </div>

        {/* Body — conversation emails by default; automated only when expanded */}
        {bodyVisible && (
          <div
            className={cn(
              'mt-1.5 whitespace-pre-wrap [overflow-wrap:anywhere] text-foreground/90 leading-relaxed',
              clampBody && 'line-clamp-3',
              expanded && htmlBody && 'whitespace-normal',
            )}
          >
            {expanded && htmlBody ? <EmailHtmlBody html={htmlBody} /> : renderedBody}
          </div>
        )}

        {showTranslation && translation && (
          <div className="mt-2 pt-2 border-t border-border text-xs italic whitespace-pre-wrap break-words text-muted-foreground">
            {translation}
          </div>
        )}

        {showToggle && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-1 flex items-center gap-0.5 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {toggleLabel}
          </button>
        )}
      </div>

      {/* Meta row below the bubble */}
      <div className="flex items-center gap-1.5 px-1">
        <span className="text-[11px] text-muted-foreground">{time}</span>
        {clientName && (
          <span className="text-[11px] text-muted-foreground">&middot; {clientName}</span>
        )}
        {isOutbound && event.user_name && (
          <span className="text-[11px] text-muted-foreground">&middot; {event.user_name}</span>
        )}
        {isOutbound && <EmailDeliveryStatusBadge metadata={event.metadata} />}
      </div>
    </div>
  );
}

// ─── Note Bubble ─────────────────────────────────────────────────────────────

interface NoteBubbleProps {
  event: CustomerEvent;
}

export function NoteBubble({ event }: NoteBubbleProps) {
  const time = new Date(event.timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className="flex justify-center py-1">
      {/* data-feed-note-id: the sidebar Notes card scrolls to and outlines this. */}
      <div
        data-feed-note-id={event.id != null ? String(event.id) : undefined}
        className="w-full max-w-md rounded-lg border border-amber-300 bg-amber-100 dark:border-amber-700 dark:bg-amber-900/40 px-3 sm:px-4 py-2.5">
        <div className="flex items-center gap-1.5 mb-1">
          <StickyNote className="h-3 w-3 text-amber-600" />
          <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">Internal note</span>
          <span className="text-[11px] text-amber-600/60 dark:text-amber-500/60 ml-auto">{time}</span>
        </div>
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{event.message}</p>
        {event.user_name && (
          <p className="text-[11px] text-muted-foreground mt-1.5">— {event.user_name}</p>
        )}
      </div>
    </div>
  );
}

// ─── System Event Bubble ─────────────────────────────────────────────────────

const systemEventLabels: Record<string, { label: string; icon: React.ElementType; variant: 'default' | 'secondary' | 'success' | 'warning' | 'info' | 'outline' }> = {
  [CustomerEventType.STATUS_CHANGE]: { label: 'Status changed', icon: Truck, variant: 'info' },
  // T-100 "Sign in SIED": one entry per attempt; the text is in `message`,
  // the outcome (signed | saved_only | already_signed | failed) in `status`.
  [CustomerEventType.SIED_SIGN]: { label: 'SIED', icon: PenTool, variant: 'info' },
  [CustomerEventType.CONTRACT_SENT]: { label: 'Contract sent', icon: FileText, variant: 'secondary' },
  [CustomerEventType.CONTRACT_OPENED]: { label: 'Contract opened', icon: Eye, variant: 'info' },
  [CustomerEventType.CONTRACT_SIGNED]: { label: 'Contract signed', icon: PenTool, variant: 'success' },
  [CustomerEventType.CONTRACT_PAID]: { label: 'Payment received', icon: CreditCard, variant: 'success' },
  [CustomerEventType.CPP_BENEFITS_SIGNED]: { label: 'CPP benefits signed', icon: PenTool, variant: 'success' },
  [CustomerEventType.CPP_BENEFITS_MAILED]: { label: 'CPP benefits mailed', icon: Send, variant: 'secondary' },
  [CustomerEventType.MAIL_SENT]: { label: 'Mail sent', icon: Mail, variant: 'secondary' },
  [CustomerEventType.REVIEW_REQUEST_SENT]: { label: 'Review request sent', icon: Star, variant: 'warning' },
  [CustomerEventType.PAPERWORK_COMPLETE_EMAIL_SENT]: { label: 'Paperwork-complete email sent', icon: Mail, variant: 'success' },
  [CustomerEventType.PAGE_VIEW]: { label: 'Page viewed', icon: Globe, variant: 'info' },
  [CustomerEventType.THANK_YOU_CARD_REQUESTED]: { label: 'Thank-you card requested by family', icon: Heart, variant: 'warning' },
  [CustomerEventType.THANK_YOU_CARD_SENT]: { label: 'Thank-you card sent', icon: Heart, variant: 'success' },
  [CustomerEventType.BENEFITS_STATUS_CHANGE]: { label: 'Benefits status changed', icon: Landmark, variant: 'success' },
  // Pre-need TruStage flow (T-162): the label is the event's own message.
  [CustomerEventType.PRENEED_TRUSTAGE_NOT_SENT]: { label: 'TruStage form not sent', icon: FileText, variant: 'warning' },
  [CustomerEventType.PRENEED_CERTIFICATE_RECEIVED]: { label: 'TruStage certificate received', icon: FileText, variant: 'success' },
  [CustomerEventType.PRENEED_CERTIFICATE_TO_CONFIRM]: { label: 'Possible TruStage certificate', icon: FileText, variant: 'warning' },
  [CustomerEventType.PRENEED_CERTIFICATE_DISMISSED]: { label: 'Not a certificate', icon: FileText, variant: 'secondary' },
  [CustomerEventType.PRENEED_PACKAGE_SCHEDULED]: { label: 'Confirmation package scheduled', icon: Send, variant: 'info' },
  [CustomerEventType.PRENEED_PACKAGE_NOT_SCHEDULED]: { label: 'Confirmation package not scheduled', icon: Send, variant: 'warning' },
  [CustomerEventType.PRENEED_PACKAGE_HELD]: { label: 'Confirmation package held', icon: Send, variant: 'secondary' },
  [CustomerEventType.PRENEED_PACKAGE_SHADOW]: { label: 'Confirmation package (shadow)', icon: Send, variant: 'secondary' },
  [CustomerEventType.PRENEED_PACKAGE_SENT]: { label: 'Confirmation package sent', icon: Send, variant: 'success' },
  [CustomerEventType.PRENEED_POLICY_NUMBER_SET]: { label: 'TruStage policy number set', icon: FileText, variant: 'secondary' },
  [CustomerEventType.PRENEED_MARKED_ACTIVE]: { label: 'Plan marked Active', icon: FileText, variant: 'success' },
  [CustomerEventType.PRENEED_ACTIVE_UNDONE]: { label: 'Active undone', icon: FileText, variant: 'warning' },
  // TruStage claim at fruition (T-174).
  [CustomerEventType.PRENEED_CLAIM_APPROVED]: { label: 'TruStage claim approved', icon: PenTool, variant: 'info' },
  [CustomerEventType.PRENEED_CLAIM_FORM_SENT]: { label: 'TruStage claim sent for signature', icon: Send, variant: 'secondary' },
  [CustomerEventType.PRENEED_CLAIM_SIGNED]: { label: 'TruStage claim signed', icon: PenTool, variant: 'success' },
  [CustomerEventType.PRENEED_CLAIM_SENT]: { label: 'Claim sent to TruStage', icon: Send, variant: 'success' },
  [CustomerEventType.PRENEED_CLAIM_SEND_FAILED]: { label: 'TruStage claim not sent', icon: Send, variant: 'warning' },
  [CustomerEventType.PRENEED_CLAIM_PAID]: { label: 'TruStage claim paid', icon: CreditCard, variant: 'success' },
  [CustomerEventType.PRENEED_CLAIM_OVERDUE]: { label: 'TruStage claim overdue', icon: FileText, variant: 'warning' },
};

function formatStatusLabel(status?: CremationStatus): string {
  if (!status) return '';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface SystemEventBubbleProps {
  event: CustomerEvent;
}

const PRENEED_EVENT_TYPES = new Set<CustomerEventType>([
  CustomerEventType.PRENEED_TRUSTAGE_NOT_SENT,
  CustomerEventType.PRENEED_CERTIFICATE_RECEIVED,
  CustomerEventType.PRENEED_CERTIFICATE_TO_CONFIRM,
  CustomerEventType.PRENEED_CERTIFICATE_DISMISSED,
  CustomerEventType.PRENEED_PACKAGE_SCHEDULED,
  CustomerEventType.PRENEED_PACKAGE_NOT_SCHEDULED,
  CustomerEventType.PRENEED_PACKAGE_HELD,
  CustomerEventType.PRENEED_PACKAGE_SHADOW,
  CustomerEventType.PRENEED_PACKAGE_SENT,
  CustomerEventType.PRENEED_POLICY_NUMBER_SET,
  CustomerEventType.PRENEED_MARKED_ACTIVE,
  CustomerEventType.PRENEED_ACTIVE_UNDONE,
  CustomerEventType.PRENEED_CLAIM_APPROVED,
  CustomerEventType.PRENEED_CLAIM_FORM_SENT,
  CustomerEventType.PRENEED_CLAIM_SIGNED,
  CustomerEventType.PRENEED_CLAIM_SENT,
  CustomerEventType.PRENEED_CLAIM_SEND_FAILED,
  CustomerEventType.PRENEED_CLAIM_PAID,
  CustomerEventType.PRENEED_CLAIM_OVERDUE,
]);

/** Status-change events that should render as prominent milestone cards */
const STATUS_CHANGE_TYPES = new Set<CustomerEventType>([
  CustomerEventType.STATUS_CHANGE,
  CustomerEventType.CONTRACT_SENT,
  CustomerEventType.CONTRACT_OPENED,
  CustomerEventType.CONTRACT_SIGNED,
  CustomerEventType.CONTRACT_PAID,
]);

function formatMilestoneDate(timestamp: string): string {
  const d = new Date(timestamp);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }) + ' at ' + d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).toLowerCase();
}

export function SystemEventBubble({ event }: SystemEventBubbleProps) {
  const config = systemEventLabels[event.type];
  const time = new Date(event.timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (!config) {
    return (
      <div className="flex justify-center py-1">
        <Badge variant="outline" className="text-[11px] font-normal gap-1.5 py-1">
          {event.type.replace(/_/g, ' ')}
          <span className="text-muted-foreground">{time}</span>
        </Badge>
      </div>
    );
  }

  const Icon = config.icon;
  let label = config.label;
  let variant = config.variant;
  if (event.type === CustomerEventType.BENEFITS_STATUS_CHANGE) {
    // `status` holds the benefits state here, not a cremation status.
    const benefitsState = event.status as string | undefined;
    label = benefitsTimelineLabel(benefitsState, event.message);
    if (benefitsState === 'ineligible') variant = 'warning';
  } else if (event.type === CustomerEventType.STATUS_CHANGE && event.status) {
    label = formatStatusLabel(event.status);
  } else if (event.type === CustomerEventType.PAGE_VIEW && event.subject) {
    label = event.subject;
  } else if (PRENEED_EVENT_TYPES.has(event.type) && event.message) {
    label = event.message;
  } else if (event.type === CustomerEventType.SIED_SIGN && event.message) {
    label = event.message;
    const outcome = event.status as string | undefined;
    variant = outcome === 'signed' || outcome === 'already_signed' ? 'success' : outcome === 'failed' ? 'warning' : 'info';
  }

  // Prominent milestone card for status-change events
  if (STATUS_CHANGE_TYPES.has(event.type)) {
    return (
      <div className="flex justify-center py-2">
        <div className="flex flex-col items-center gap-1 rounded-xl border bg-background px-5 py-3 shadow-sm w-full max-w-sm">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            <span className="text-sm font-semibold">{label}</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {formatMilestoneDate(event.timestamp)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center py-1">
      <Badge variant={variant} className="text-[11px] font-normal gap-1.5 py-1 px-3 max-w-full text-center flex-wrap">
        <Icon className="h-3 w-3" />
        {label}
        <span className="opacity-60">{time}</span>
      </Badge>
    </div>
  );
}
